import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ElmMark from "@/components/ElmMark";
import { AREAS, GLOSSARY, LESSONS, QA, tierLabel } from "@/lib/learn";

export default function LearnPrint({ doc = "guide" }) {
  const { brand } = useAuth();
  const isGuide = doc === "guide";
  const title = isGuide ? "E.L.M Study Guide" : "E.L.M Builder Q&A";
  const areas = AREAS.filter(([k]) => LESSONS.some((l) => l.area === k));
  return (
    <div className="min-h-screen bg-white text-stone-900" data-testid={`learn-print-${doc}`}>
      <div className="mx-auto flex max-w-[8.5in] items-center justify-between px-6 py-4 print:hidden">
        <Link to="/learn" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="learn-print-back"><ArrowLeft className="h-4 w-4" />Back to Learn</Link>
        <Button size="sm" className="rounded-full" onClick={() => window.print()} data-testid="learn-print-button"><Printer className="mr-1.5 h-3.5 w-3.5" />Print / Save PDF</Button>
      </div>
      <main className="mx-auto max-w-[8.5in] px-8 pb-12 pt-2 print:px-0 print:pt-0">
        <header className="flex items-start justify-between border-b border-stone-900 pb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">{brand?.name || "E.L.M"} · training</p>
            <h1 className="mt-1 font-serif text-3xl leading-none sm:text-4xl" data-testid="learn-print-title">{title}</h1>
            <p className="mt-2 text-sm text-stone-600">{isGuide ? "A plain-language guide to every part of E.L.M, by area." : "The questions builders and evaluators ask, with straight answers."}</p>
          </div>
          <ElmMark className="shrink-0" />
        </header>

        {isGuide ? areas.map(([key, name]) => (
          <section key={key} className="mt-6 break-inside-avoid">
            <h2 className="font-serif text-2xl text-stone-900">{name}</h2>
            {LESSONS.filter((l) => l.area === key).map((l) => (
              <article key={l.id} className="mt-4 break-inside-avoid">
                <div className="flex items-baseline justify-between gap-3 border-b border-stone-200 pb-1">
                  <h3 className="text-base font-semibold">{l.title}</h3>
                  <span className="text-[10px] uppercase tracking-wider text-stone-400">{tierLabel(l.tier)} · {l.who}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed">{l.what}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-stone-600"><span className="font-medium text-stone-900">Why it matters: </span>{l.why}</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{l.points.map((p) => <li key={p}>{p}</li>)}</ul>
              </article>
            ))}
          </section>
        )) : (
          <section className="mt-6">
            {[...new Set(QA.map((x) => x.topic))].map((topic) => (
              <div key={topic} className="mt-5 break-inside-avoid">
                <h2 className="font-serif text-2xl text-stone-900">{topic}</h2>
                {QA.filter((x) => x.topic === topic).map((x) => (
                  <div key={x.q} className="mt-3 break-inside-avoid">
                    <p className="text-sm font-semibold">{x.q}</p>
                    <p className="mt-1 text-sm leading-relaxed text-stone-600">{x.a}</p>
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}
        <footer className="mt-10 border-t border-stone-300 pt-3 text-xs text-stone-500">Prepared with E.L.M · Elevated Living Management</footer>
      </main>
    </div>
  );
}
